/**
 * User Service
 * Handles user management API calls
 */

import api from './api';

const userService = {
  /**
   * Get all users (platform admin - all tenants)
   * @returns {Promise<Array>} List of all users
   */
  getAllUsers: async () => {
    try {
      const response = await api.get('/v1/admin/users');
      return response.data.users || response.data;
    } catch (error) {
      // Fallback demo data
      return [
        { id: '1', name: 'John Doe', email: 'john@ourbus.com', role: 'tenant_admin', tenant_id: 'ourbus', status: 'active', last_login: '2026-03-15T10:00:00Z' },
        { id: '2', name: 'Jane Smith', email: 'jane@ourbus.com', role: 'tenant_ops', tenant_id: 'ourbus', status: 'active', last_login: '2026-03-14T15:30:00Z' },
        { id: '3', name: 'Mike Brown', email: 'mike@redbus.com', role: 'tenant_owner', tenant_id: 'redbus', status: 'active', last_login: '2026-03-15T08:00:00Z' },
        { id: '4', name: 'Sarah Lee', email: 'sarah@redbus.com', role: 'tenant_user', tenant_id: 'redbus', status: 'active', last_login: '2026-03-13T12:00:00Z' },
        { id: '5', name: 'Admin User', email: 'admin@servicehive.tech', role: 'super_admin', tenant_id: null, status: 'active', last_login: '2026-03-15T09:00:00Z' },
      ];
    }
  },

  /**
   * Get users for current tenant
   * @returns {Promise<Array>} List of tenant users
   */
  getTenantUsers: async () => {
    try {
      const response = await api.get('/v1/users');
      return response.data.users || response.data;
    } catch (error) {
      return [
        { id: '1', name: 'John Doe', email: 'john@company.com', role: 'tenant_admin', status: 'active' },
        { id: '2', name: 'Jane Smith', email: 'jane@company.com', role: 'tenant_ops', status: 'active' },
      ];
    }
  },

  /**
   * Get a specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User details
   */
  getUser: async (userId) => {
    const response = await api.get(`/v1/users/${userId}`);
    return response.data;
  },

  /**
   * Create a new user
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Created user
   */
  createUser: async (userData) => {
    const response = await api.post('/v1/users', userData);
    return response.data;
  },

  /**
   * Update a user
   * @param {string} userId - User ID
   * @param {Object} updates - Update data
   * @returns {Promise<Object>} Updated user
   */
  updateUser: async (userId, updates) => {
    const response = await api.put(`/v1/users/${userId}`, updates);
    return response.data;
  },

  /**
   * Delete/deactivate a user
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  deleteUser: async (userId) => {
    await api.delete(`/v1/users/${userId}`);
  },

  /**
   * Send invite email to user
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  sendInvite: async (userId) => {
    await api.post(`/v1/users/${userId}/invite`);
  },

  /**
   * Reset user password
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  resetPassword: async (userId) => {
    await api.post(`/v1/users/${userId}/reset-password`);
  },

  /**
   * Update user role
   * @param {string} userId - User ID
   * @param {string} role - New role
   * @returns {Promise<Object>} Updated user
   */
  updateRole: async (userId, role) => {
    const response = await api.put(`/v1/users/${userId}/role`, { role });
    return response.data;
  },
};

export default userService;

